import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

/**
 * Airtime & data operations.
 *
 * Everything on this page is internal: the platform float, what each sale
 * actually earned, and orders where someone is out of pocket. None of it
 * appears anywhere a consultant can see.
 */

interface FloatSnapshot {
  balance: number | null;
  retrieved_at: string | null;
  threshold: number;
  is_low: boolean;
  is_critical: boolean;
  configured?: boolean;
  /** Present when the balance could not be read — says why, in plain terms. */
  unavailable_reason?: string;
}

interface VtuOrder {
  id: number;
  reference: string;
  pairgate_reference: string | null;
  user_id: number;
  type: 'airtime' | 'data';
  status: string;
  provider_slug: string;
  plan_label: string | null;
  recipient: string;
  list_price: string;
  sell_price: string;
  cost_price: string | null;
  markup_margin: string | null;
  commission_accrued: string | null;
  margin_rate: string | null;
  payment_method: string;
  test_mode: boolean;
  failure_reason: string | null;
  created_at: string;
}

interface MarginRow {
  type: string;
  provider: string;
  orders: number;
  gross: string;
  cost: string;
  markup: string;
  commission: string;
}

interface MarginReport {
  since: string;
  rows: MarginRow[];
  unprocessed_webhooks: number;
}

interface CatalogueStatus {
  plans: number;
  active: number;
  last_synced_at: string | null;
}

const STATUS_TABS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'Processing', value: 'processing' },
  { label: 'Successful', value: 'successful' },
  { label: 'Failed', value: 'failed' },
  { label: 'Refunded', value: 'refunded' },
  { label: 'Needs action', value: 'reversal_failed' },
];

function formatNaira(value: string | number | null | undefined) {
  if (value == null) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2,
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatPercent(value: string | number | null | undefined) {
  if (value == null) return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(2)}%`;
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === 'successful'
      ? 'bg-emerald-100 text-emerald-700'
      : status === 'processing'
        ? 'bg-blue-100 text-blue-700'
        : status === 'refunded'
          ? 'bg-amber-100 text-amber-700'
          : status === 'reversal_failed'
            ? 'bg-red-100 text-red-700'
            : status === 'failed'
              ? 'bg-slate-200 text-slate-700'
              : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${tone}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

/** Float widget. Amber below threshold, red below a quarter of it. */
function FloatCard({ float }: { float: FloatSnapshot | null }) {
  if (!float) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-xs uppercase text-slate-500">Pairgate float</div>
        <div className="mt-1 text-sm text-slate-400">Loading…</div>
      </div>
    );
  }

  // Not configured is a setup problem, not a low balance — say so plainly
  // rather than showing ₦0, which would read as "we have run out".
  if (float.configured === false || float.balance == null) {
    return (
      <div className="rounded-lg border border-slate-300 bg-slate-50 p-4">
        <div className="text-xs uppercase text-slate-500">Pairgate float</div>
        <div className="mt-1 text-lg font-semibold text-slate-700">
          Unavailable
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {float.unavailable_reason ??
            'The balance could not be read right now.'}
        </div>
      </div>
    );
  }

  const tone = float.is_critical
    ? 'border-red-300 bg-red-50'
    : float.is_low
      ? 'border-amber-300 bg-amber-50'
      : 'border-slate-200 bg-white';

  return (
    <div className={`rounded-lg border p-4 ${tone}`}>
      <div className="text-xs uppercase text-slate-500">Pairgate float</div>
      <div className="mt-1 text-2xl font-semibold text-slate-900">
        {formatNaira(float.balance)}
      </div>
      <div className="mt-1 text-xs text-slate-500">
        Alert below {formatNaira(float.threshold)} · updated{' '}
        {formatDate(float.retrieved_at)}
      </div>
      {float.is_low && (
        <div className="mt-2 text-xs font-medium text-red-700">
          Top up. Purchases fail and refund once this runs out.
        </div>
      )}
    </div>
  );
}

export function VtuPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('vtu.manage');

  const [float, setFloat] = useState<FloatSnapshot | null>(null);
  const [orders, setOrders] = useState<VtuOrder[]>([]);
  const [report, setReport] = useState<MarginReport | null>(null);
  const [catalogue, setCatalogue] = useState<CatalogueStatus | null>(null);
  const [tab, setTab] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = tab ? `?status=${encodeURIComponent(tab)}` : '';
      const [balanceRes, ordersRes, reportRes, catalogueRes] =
        await Promise.all([
          api.get<FloatSnapshot>('/back-office/vtu/balance'),
          api.get<VtuOrder[]>(`/back-office/vtu/orders${query}`),
          api.get<MarginReport>('/back-office/vtu/margin-report'),
          api.get<CatalogueStatus>('/back-office/vtu/catalogue/status'),
        ]);
      setFloat(balanceRes.data);
      setOrders(Array.isArray(ordersRes.data) ? ordersRes.data : []);
      setReport(reportRes.data);
      setCatalogue(catalogueRes.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load VTU data'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const syncCatalogue = async () => {
    setSyncing(true);
    try {
      await api.post('/back-office/vtu/catalogue/sync', {});
      await load();
    } catch (err) {
      setError(extractErrorMessage(err, 'Catalogue sync failed'));
    } finally {
      setSyncing(false);
    }
  };

  /** Realised vs configured, so a collapsed upstream rate is obvious. */
  const totals = useMemo(() => {
    const rows = report?.rows ?? [];
    const sum = (key: keyof MarginRow) =>
      rows.reduce((acc, row) => acc + Number(row[key] ?? 0), 0);
    const gross = sum('gross');
    const margin = sum('markup') + sum('commission');
    return {
      orders: rows.reduce((acc, row) => acc + row.orders, 0),
      gross,
      cost: sum('cost'),
      markup: sum('markup'),
      commission: sum('commission'),
      margin,
      rate: gross > 0 ? margin / gross : 0,
    };
  }, [report]);

  const needsAction = orders.filter((o) => o.status === 'reversal_failed');

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Airtime &amp; Data
          </h1>
          <p className="text-sm text-slate-500">
            Platform float, realised margin and order history.
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={syncCatalogue}
            disabled={syncing}
            className="rounded bg-slate-900 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {syncing ? 'Syncing…' : 'Sync catalogue'}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {needsAction.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <strong>{needsAction.length} order(s) need manual refunds.</strong>{' '}
          The consultant was charged, the purchase failed, and the reversal also
          failed — they are out of pocket until someone returns the money.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <FloatCard float={float} />

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500">
            Margin (30 days)
          </div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">
            {formatNaira(totals.margin)}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            {formatPercent(totals.rate)} of {formatNaira(totals.gross)} sold ·{' '}
            {totals.orders} orders
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500">
            Commission owed
          </div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">
            {formatNaira(totals.commission)}
          </div>
          {/* Not spendable float — it sits in Pairgate's commission wallet
              until someone withdraws it. */}
          <div className="mt-1 text-xs text-slate-500">
            Accrued in Pairgate&apos;s commission wallet. Withdraw manually.
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase text-slate-500">Catalogue</div>
          <div className="mt-1 text-2xl font-semibold text-slate-900">
            {catalogue?.active ?? '—'}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            active plans · synced {formatDate(catalogue?.last_synced_at)}
          </div>
          {report && report.unprocessed_webhooks > 0 && (
            <div className="mt-2 text-xs font-medium text-amber-700">
              {report.unprocessed_webhooks} webhook(s) unprocessed
            </div>
          )}
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="text-sm font-semibold text-slate-900">
            Realised margin by product
          </h2>
          <p className="text-xs text-slate-500">
            What we actually earned. Successful, non-test orders only.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Product</th>
                <th className="px-4 py-2">Network</th>
                <th className="px-4 py-2 text-right">Orders</th>
                <th className="px-4 py-2 text-right">Sold</th>
                <th className="px-4 py-2 text-right">Cost</th>
                <th className="px-4 py-2 text-right">Markup</th>
                <th className="px-4 py-2 text-right">Commission</th>
                <th className="px-4 py-2 text-right">Realised</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {(report?.rows ?? []).map((row) => {
                const gross = Number(row.gross);
                const margin = Number(row.markup) + Number(row.commission);
                return (
                  <tr
                    key={`${row.type}-${row.provider}`}
                    className="border-b border-slate-100 last:border-b-0"
                  >
                    <td className="px-4 py-2 capitalize">{row.type}</td>
                    <td className="px-4 py-2 uppercase">{row.provider}</td>
                    <td className="px-4 py-2 text-right">{row.orders}</td>
                    <td className="px-4 py-2 text-right">
                      {formatNaira(row.gross)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {formatNaira(row.cost)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {formatNaira(row.markup)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {formatNaira(row.commission)}
                    </td>
                    <td className="px-4 py-2 text-right font-medium">
                      {formatPercent(gross > 0 ? margin / gross : 0)}
                    </td>
                  </tr>
                );
              })}
              {!report?.rows?.length && (
                <tr>
                  <td className="px-4 py-6 text-slate-400" colSpan={8}>
                    No completed sales yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
          {STATUS_TABS.map((t) => (
            <button
              key={t.value || 'all'}
              type="button"
              onClick={() => setTab(t.value)}
              className={`rounded px-2.5 py-1 text-xs font-medium ${
                tab === t.value
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600'
              }`}
            >
              {t.label}
            </button>
          ))}
          {loading && (
            <span className="ml-auto text-xs text-slate-400">Loading…</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Reference</th>
                <th className="px-4 py-2">User</th>
                <th className="px-4 py-2">Item</th>
                <th className="px-4 py-2">Recipient</th>
                <th className="px-4 py-2 text-right">Sold</th>
                <th className="px-4 py-2 text-right">Cost</th>
                <th className="px-4 py-2 text-right">Margin</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {orders.map((order) => {
                const margin =
                  Number(order.markup_margin ?? 0) +
                  Number(order.commission_accrued ?? 0);
                return (
                  <tr
                    key={order.id}
                    className="border-b border-slate-100 last:border-b-0"
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-xs">
                      {formatDate(order.created_at)}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {order.reference}
                      {order.test_mode && (
                        <span className="ml-1 rounded bg-slate-200 px-1 text-[10px] uppercase">
                          test
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{order.user_id}</td>
                    <td className="px-4 py-2">{order.plan_label}</td>
                    <td className="px-4 py-2 font-mono text-xs">
                      {order.recipient}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {formatNaira(order.sell_price)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {formatNaira(order.cost_price)}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {order.cost_price ? formatNaira(margin) : '—'}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={order.status} />
                      {order.failure_reason && (
                        <div className="mt-0.5 text-[10px] text-slate-500">
                          {order.failure_reason}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!orders.length && !loading && (
                <tr>
                  <td className="px-4 py-6 text-slate-400" colSpan={9}>
                    No orders.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
